import React, { useEffect, useRef } from "react";
import Select from "react-select";
import html2pdf from "html2pdf.js";

const toOpts = (arr) => (arr || []).map((v) => ({ value: String(v), label: String(v) }));

/* ══════════════════════════════════════════════════════════════
   PRODUCT FORMAT TAB
   Filter bar + paginated list + the product-format modal (parameters /
   docs) + the report view popup. Data is already paginated server-side
   by the parent (loadProductFormats) — this component only ever
   renders the current page.
══════════════════════════════════════════════════════════════ */
function InspectionProductFormatTab({
  styles, filterSelectStyles, DeleteIcon, ddl, sbeeLogo,
  cascadedCableTypes, cascadedCodes,

  productFormatList, pfLoading,
  pfFilterSelections, setPfFilterSelections, pfFilterOptions,
  pfTemplateNoFilter, setPfTemplateNoFilter,
  pfInspTestTypeFilter, setPfInspTestTypeFilter,
  pfProductCodeFilter, setPfProductCodeFilter,
  pfProductDescFilter, setPfProductDescFilter,
  lookupProductByCode, lookupProductByDescription,
  pfActiveFilterCount,
  pfPage, setPfPage, pfTotal, PF_PAGE_SIZE,

  handleViewProductFormat, handleDeleteProductFormat,

  showPfModal, setShowPfModal, pfEditRow,
  pfModalTab, setPfModalTab,
  isStdValueEditMode, setIsStdValueEditMode,
  pfStdValues, setPfStdValues, normChar,
  handleProductStdValueSave,
  pfReportRef,
  showPfViewModal, setShowPfViewModal,
}) {
  /* Product Code ↔ Product Description auto-fill.
     Mirrors the same lookupProductByCode/lookupProductByDescription
     wiring the Assignment tab already uses — this tab's two text
     filters were previously independent, so typing a product code
     never populated the description (and vice versa). Each side only
     fills the OTHER field when it's still empty, so it never clobbers
     something the user is deliberately typing into both boxes to
     narrow the list. */
  const pfCodeLookupRef = useRef(null);
  useEffect(() => {
    if (!lookupProductByCode) return;
    if (pfCodeLookupRef.current) clearTimeout(pfCodeLookupRef.current);
    if (!pfProductCodeFilter || pfProductDescFilter) return;
    pfCodeLookupRef.current = setTimeout(async () => {
      const match = await lookupProductByCode(pfProductCodeFilter);
      if (match?.productDescription) setPfProductDescFilter(match.productDescription);
    }, 400);
    return () => clearTimeout(pfCodeLookupRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfProductCodeFilter]);

  const pfDescLookupRef = useRef(null);
  useEffect(() => {
    if (!lookupProductByDescription) return;
    if (pfDescLookupRef.current) clearTimeout(pfDescLookupRef.current);
    if (!pfProductDescFilter || pfProductCodeFilter) return;
    pfDescLookupRef.current = setTimeout(async () => {
      const match = await lookupProductByDescription(pfProductDescFilter);
      if (match?.productCode) setPfProductCodeFilter(match.productCode);
    }, 400);
    return () => clearTimeout(pfDescLookupRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfProductDescFilter]);

  const renderFilterBar = () => (
    <div style={styles.filterCard}>
      {/* Row 1: Product Type */}
      <div style={styles.filterBar}>
        {[{ key: "product_type", label: "Product Type" }].map(({ key, label }) => {
          // Now sourced from the actual inspection_assignments table
          // (via /product-formats/filter-options), not the config
          // master list — so this only ever offers product types that
          // actually have rows to filter on.
          const uniqueVals = pfFilterOptions[key] || [];
          return (
            <div key={key} style={{ minWidth: 150, flex: "0 0 260px", display: "flex", flexDirection: "column", gap: 3 }}>
              <span style={styles.filterLabel}>{label}</span>
              <Select
                isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
                options={uniqueVals.map(v => ({ value: v, label: v }))}
                value={pfFilterSelections[key]}
                onChange={(opts) => setPfFilterSelections(prev => ({ ...prev, [key]: opts || [] }))}
                placeholder={label} isClearable
                menuPortalTarget={document.body} menuPosition="fixed"
                styles={filterSelectStyles}
              />
            </div>
          );
        })}
      </div>

      {/* Row 2: Category, Sub Category, Cable Type, Code, Color etc. */}
      <div style={{ ...styles.filterBar, marginTop: 10, flexWrap: "wrap" }}>
        {[
          { key: "product_category", label: "Category"     },
          { key: "sub_category",     label: "Sub Category" },
          { key: "cable_type",       label: "Cable Type"   },
          { key: "code",             label: "Code"         },
          { key: "color",            label: "Color"        },
          { key: "cores",            label: "Cores"        },
          { key: "cross_section",    label: "Cross Section"},
          { key: "std_ref",          label: "Std Ref"      },
          { key: "voltage",          label: "Voltage"      },
          { key: "construction",     label: "Construction" },
        ].map(({ key, label }) => {
          // From the dedicated filter-options endpoint, not from
          // productFormatList — that only ever holds the current
          // page now, so it can't supply the full set of values.
          const uniqueVals = pfFilterOptions[key] || [];
          return (
            <div key={key} style={{ minWidth: 100, flex: "1 1 100px", maxWidth: 170, display: "flex", flexDirection: "column", gap: 3 }}>
              <span style={styles.filterLabel}>{label}</span>
              <Select
                isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
                options={uniqueVals.map(v => ({ value: v, label: v }))}
                value={pfFilterSelections[key]}
                onChange={(opts) => setPfFilterSelections(prev => ({ ...prev, [key]: opts || [] }))}
                placeholder={label} isClearable
                menuPortalTarget={document.body} menuPosition="fixed"
                styles={filterSelectStyles}
              />
            </div>
          );
        })}
      </div>

      {/* Row 3: Product Code + Description */}
      <div style={{ display: "flex", gap: 12, marginTop: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={styles.filterLabel}>Product Code</span>
          <input type="text" className="form-control form-control-sm" placeholder="Search product code..."
            style={{ width: 170, height: 30, fontSize: 12 }} value={pfProductCodeFilter}
            onChange={e => setPfProductCodeFilter(e.target.value)} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 220 }}>
          <span style={styles.filterLabel}>Product Description</span>
          <input type="text" className="form-control form-control-sm" placeholder="Search product description..."
            style={{ height: 30, fontSize: 12 }} value={pfProductDescFilter}
            onChange={e => setPfProductDescFilter(e.target.value)} />
        </div>
      </div>

      {/* Row 4: Template Number + Inspection Test Type + Clear */}
      <div style={{ display: "flex", gap: 12, marginTop: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={styles.filterLabel}>Template Number</span>
          <input type="text" className="form-control form-control-sm" placeholder="Search template number..."
            style={{ width: 170, height: 30, fontSize: 12 }} value={pfTemplateNoFilter}
            onChange={e => setPfTemplateNoFilter(e.target.value)} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={styles.filterLabel}>Inspection Test Type</span>
          <input type="text" className="form-control form-control-sm" placeholder="Search inspection test type..."
            style={{ width: 180, height: 30, fontSize: 12 }} value={pfInspTestTypeFilter}
            onChange={e => setPfInspTestTypeFilter(e.target.value)} />
        </div>
        {pfActiveFilterCount > 0 && (
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <button
              onClick={() => {
                setPfFilterSelections({
                  product_type: [], product_category: [], sub_category: [], quality_category: [],
                  cable_type: [], code: [], color: [], cores: [], cross_section: [],
                  std_ref: [], voltage: [], construction: []
                });
                setPfTemplateNoFilter("");
                setPfInspTestTypeFilter("");
                setPfProductCodeFilter("");
                setPfProductDescFilter("");
              }}
              style={styles.clearBtn}
            >✕ Clear ({pfActiveFilterCount})</button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {renderFilterBar()}

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table className="table" style={{ ...styles.erpTable, fontSize: 12, whiteSpace: "nowrap" }}>
          <thead>
            <tr>
              <th style={styles.erpTh}>ID</th>
              <th style={styles.erpTh}>Product Code</th>
              <th style={styles.erpTh}>Product Description</th>
              <th style={styles.erpTh}>Product Type</th>
              <th style={styles.erpTh}>Template No.</th>
              <th style={styles.erpTh}>Inspection Test Type</th>
              <th style={styles.erpTh}>Category</th>
              <th style={styles.erpTh}>Sub Category</th>
              <th style={styles.erpTh}>QA Category</th>
              <th style={styles.erpTh}>Cable Type</th>
              <th style={styles.erpTh}>Construction</th>
              <th style={styles.erpTh}>Colour</th>
              <th style={styles.erpTh}>Cores</th>
              <th style={styles.erpTh}>Cross Section</th>
              <th style={styles.erpTh}>Std. Ref</th>
              <th style={styles.erpTh}>Voltage</th>
              <th style={styles.erpTh}>Code</th>
              <th style={styles.erpTh}>Sequence</th>
              <th style={{ ...styles.erpTh, textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {pfLoading ? (
              <tr><td colSpan="19" className="text-center text-muted p-3">Loading…</td></tr>
            ) : productFormatList.length > 0 ? productFormatList.map(row => (
              <tr key={row.id}>
                <td style={styles.erpTd}>{row.id}</td>
                <td style={styles.erpTd}>{row.product_code || "—"}</td>
                <td style={styles.erpTd}>{row.product_description || "—"}</td>
                <td style={styles.erpTd}>{row.product_type || "—"}</td>
                <td style={styles.erpTd}>{row.inspection_no || "—"}</td>
                <td style={styles.erpTd}>{row.inspection_test_type || "—"}</td>
                <td style={styles.erpTd}>{row.product_category || "—"}</td>
                <td style={styles.erpTd}>{row.sub_category || "—"}</td>
                <td style={styles.erpTd}>{row.quality_category || "—"}</td>
                <td style={styles.erpTd}>{row.cable_type || "—"}</td>
                <td style={styles.erpTd}>{row.construction || "—"}</td>
                <td style={styles.erpTd}>{row.color || "—"}</td>
                <td style={styles.erpTd}>{row.cores || "—"}</td>
                <td style={styles.erpTd}>{row.cross_section || "—"}</td>
                <td style={styles.erpTd}>{row.std_ref || "—"}</td>
                <td style={styles.erpTd}>{row.voltage || "—"}</td>
                <td style={styles.erpTd}>{row.code || "—"}</td>
                <td style={styles.erpTd}>{row.sequence ?? "—"}</td>
                <td style={{ ...styles.erpTd, textAlign: "right", display: "flex", gap: 6 }}>
                  <button className="btn btn-outline-dark btn-sm"
                    onClick={() => handleViewProductFormat(row)}>
                    View
                  </button>
                  <button style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, color: "#dc2626", cursor: "pointer", padding: "4px 7px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                    onClick={() => handleDeleteProductFormat(row.id)} title="Delete">
                    <DeleteIcon />
                  </button>
                </td>
              </tr>
            )) : (
              <tr><td colSpan="19" className="text-center text-muted p-3">No product formats found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination — only the current page's rows are ever fetched/rendered */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
        <span style={{ fontSize: 12, color: "#64748b" }}>
          {pfTotal > 0
            ? `${(pfPage - 1) * PF_PAGE_SIZE + 1}–${Math.min(pfPage * PF_PAGE_SIZE, pfTotal)} of ${pfTotal}`
            : "0 results"}
        </span>
        <button className="btn btn-outline-secondary btn-sm" disabled={pfPage <= 1 || pfLoading}
          onClick={() => setPfPage(p => Math.max(1, p - 1))}>‹ Prev</button>
        <span style={{ fontSize: 12, color: "#1e293b" }}>
          Page {pfPage} of {Math.max(1, Math.ceil(pfTotal / PF_PAGE_SIZE))}
        </span>
        <button className="btn btn-outline-secondary btn-sm"
          disabled={pfLoading || pfPage >= Math.ceil(pfTotal / PF_PAGE_SIZE)}
          onClick={() => setPfPage(p => p + 1)}>Next ›</button>
      </div>

      {/* ═══════════════════════════════════════════════
          MODAL — PRODUCT FORMAT (parameters / docs)
      ═══════════════════════════════════════════════ */}
      {showPfModal && pfEditRow && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div>
                <h5 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>{pfEditRow.product_code}</h5>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  {[["parameters", "Parameters"], ["docs", "Docs"]].map(([key, label]) => (
                    <button key={key} onClick={() => setPfModalTab(key)}
                      style={{ border: "none", background: "transparent", padding: "8px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                        color: pfModalTab === key ? "#021843" : "#64748b",
                        borderBottom: pfModalTab === key ? "2px solid #021843" : "2px solid transparent", marginBottom: -1 }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <button style={styles.closeBtn} onClick={() => { setShowPfModal(false); setIsStdValueEditMode(false); }}>✖</button>
            </div>

            <div style={{ ...styles.modalBody, padding: "24px 28px" }}>
              {/* ── PARTICULARS TAB ── */}
              {pfModalTab === "parameters" && (
                <>
                  {(pfEditRow.product_type || "").toLowerCase().includes("finished") ? (() => {
                    const grouped = (pfEditRow.characteristics || []).reduce((acc, c) => {
                      const key = c.test_type || "General";
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(c);
                      return acc;
                    }, {});
                    return Object.entries(grouped).map(([testType, chars]) => (
                      <div key={testType} style={{ marginBottom: 24 }}>
                        <div style={{
                          fontWeight: 700, fontSize: 11, color: "#021843",
                          padding: "6px 12px", background: "#e8edf5",
                          borderRadius: 6, marginBottom: 8,
                          textTransform: "uppercase", letterSpacing: "0.05em",
                          display: "inline-block"
                        }}>
                          {testType}
                        </div>
                        <table style={styles.characteristicsTable}>
                          <thead>
                            <tr>
                              <th style={{ ...styles.tableHeader, color: "#64748b", fontWeight: 700 }}>Process</th>
                              <th style={{ ...styles.tableHeader, color: "#64748b", fontWeight: 700 }}>Characteristic</th>
                              <th style={{ ...styles.tableHeader, color: "#64748b", fontWeight: 700 }}>Variable / Formula</th>
                              <th style={{ ...styles.tableHeader, color: "#64748b", fontWeight: 700, textAlign: "center" }}>UOM</th>
                              <th style={{ ...styles.tableHeader, color: "#64748b", fontWeight: 700, textAlign: "center" }}>Standard Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {chars.map((c, i) => (
                              <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                                <td style={{ ...styles.tableCell, color: "#1e293b", fontSize: 12 }}>{c.process_name || "—"}</td>
                                <td style={{ ...styles.tableCell, fontWeight: 500 }}>{c.characteristic}</td>
                                <td style={{ ...styles.tableCell, fontSize: 12 }}>
                                  {c.isFormula
                                    ? <span title="Formula" style={{ fontFamily: "monospace", background: "#eef2ff", color: "#3730a3", padding: "2px 6px", borderRadius: 4 }}>ƒ = {c.formula || "—"}</span>
                                    : (c.variable || "—")}
                                </td>
                                <td style={{ ...styles.tableCellUom, textAlign: "center" }}>{c.uom || "—"}</td>
                                <td style={{ ...styles.tableCell, textAlign: "center" }}>
                                  {isStdValueEditMode
                                    ? <input type="text" className="form-control form-control-sm" style={{ textAlign: "center" }}
                                        value={pfStdValues[normChar(c.characteristic)] || ""}
                                        onChange={e => setPfStdValues(p => ({ ...p, [normChar(c.characteristic)]: e.target.value }))} />
                                    : <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 600 }}>
                                        {c.standard_value || pfStdValues[normChar(c.characteristic)] || "—"}
                                      </span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ));
                  })() : (
                    <table style={styles.characteristicsTable}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.tableHeader, color: "#64748b", fontWeight: 700 }}>Process</th>
                          <th style={{ ...styles.tableHeader, color: "#64748b", fontWeight: 700 }}>Characteristic</th>
                              <th style={{ ...styles.tableHeader, color: "#64748b", fontWeight: 700 }}>Variable / Formula</th>
                          <th style={{ ...styles.tableHeader, color: "#64748b", fontWeight: 700, textAlign: "center" }}>UOM</th>
                          <th style={{ ...styles.tableHeader, color: "#64748b", fontWeight: 700, textAlign: "center" }}>Standard Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(pfEditRow.characteristics || []).map((c, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                            <td style={{ ...styles.tableCell, color: "#1e293b", fontSize: 12 }}>{c.process_name || "—"}</td>
                            <td style={{ ...styles.tableCell, fontWeight: 500 }}>{c.characteristic}</td>
                                <td style={{ ...styles.tableCell, fontSize: 12 }}>
                                  {c.isFormula
                                    ? <span title="Formula" style={{ fontFamily: "monospace", background: "#eef2ff", color: "#3730a3", padding: "2px 6px", borderRadius: 4 }}>ƒ = {c.formula || "—"}</span>
                                    : (c.variable || "—")}
                                </td>
                            <td style={{ ...styles.tableCellUom, textAlign: "center" }}>{c.uom || "—"}</td>
                            <td style={{ ...styles.tableCell, textAlign: "center" }}>
                              {isStdValueEditMode
                                ? <input type="text" className="form-control form-control-sm" style={{ textAlign: "center" }}
                                    value={pfStdValues[normChar(c.characteristic)] || ""}
                                    onChange={e => setPfStdValues(p => ({ ...p, [normChar(c.characteristic)]: e.target.value }))} />
                                : <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 600 }}>
                                    {c.standard_value || pfStdValues[normChar(c.characteristic)] || "—"}
                                  </span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}

              {/* ── DOCS TAB ── */}
              {pfModalTab === "docs" && (
                <div>
                  <div ref={pfReportRef} style={{ position: "absolute", left: -9999, top: 0, visibility: "visible", opacity: 1, zIndex: -1 }}>
                    <PfInspectionReport row={pfEditRow} sbeeLogo={sbeeLogo} />
                  </div>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "16px 20px",
                    background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>
                      {pfEditRow.inspection_no}_InspectionReport
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn btn-dark btn-sm" onClick={() => setShowPfViewModal(true)}>View</button>
                      <button className="btn btn-dark btn-sm" onClick={async () => {
                        const original = pfReportRef.current;
                        if (!original) return;

                        const clone = original.cloneNode(true);
                        clone.style.position = "static";
                        clone.style.left = "0";
                        clone.style.top = "0";
                        clone.style.visibility = "visible";
                        clone.style.opacity = "1";
                        clone.style.display = "block";

                        const wrapper = document.createElement("div");
                        wrapper.style.position = "fixed";
                        wrapper.style.top = "0";
                        wrapper.style.left = "0";
                        wrapper.style.zIndex = "-1";
                        wrapper.style.background = "#fff";
                        wrapper.appendChild(clone);
                        document.body.appendChild(wrapper);

                        const imgs = Array.from(clone.querySelectorAll("img"));
                        await Promise.all(
                          imgs.map(
                            (img) =>
                              new Promise((resolve) => {
                                const tempImg = new Image();
                                tempImg.crossOrigin = "anonymous";
                                tempImg.onload = () => {
                                  try {
                                    const canvas = document.createElement("canvas");
                                    canvas.width = tempImg.naturalWidth || tempImg.width;
                                    canvas.height = tempImg.naturalHeight || tempImg.height;
                                    const ctx = canvas.getContext("2d");
                                    ctx.drawImage(tempImg, 0, 0);
                                    img.src = canvas.toDataURL("image/png");
                                  } catch (e) {}
                                  resolve();
                                };
                                tempImg.onerror = () => resolve();
                                tempImg.src = img.src;
                              })
                          )
                        );

                        await new Promise((resolve) => setTimeout(resolve, 150));

                        try {
                          await html2pdf().set({
                            margin: 10,
                            filename: `${pfEditRow.inspection_no}_InspectionReport.pdf`,
                            image: { type: "jpeg", quality: 0.98 },
                            html2canvas: {
                              scale: 2,
                              useCORS: true,
                              scrollX: 0,
                              scrollY: 0,
                              windowWidth: clone.scrollWidth,
                              windowHeight: clone.scrollHeight,
                            },
                            jsPDF: { unit: "mm", format: "a3", orientation: "landscape" },
                          }).from(clone).save();
                        } finally {
                          document.body.removeChild(wrapper);
                        }
                      }}>Download</button>
                      <button className="btn btn-dark btn-sm" onClick={() => {
                        const el = pfReportRef.current;
                        if (!el) return;
                        const win = window.open("", "_blank");
                        win.document.write(`<html><head><title>Print</title><style>body{margin:0;font-family:Calibri,sans-serif}</style></head><body>${el.innerHTML}</body></html>`);
                        win.document.close(); win.focus(); win.print();
                      }}>Print</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              {pfModalTab === "parameters" && (
                isStdValueEditMode ? (
                  <div style={{ display: "flex", gap: 10 }}>
                    <button style={{ ...styles.saveBtn, background: "#64748b" }} onClick={() => setIsStdValueEditMode(false)}>Cancel</button>
                    <button style={styles.saveBtn} onClick={handleProductStdValueSave}>Save</button>
                  </div>
                ) : (
                  <button style={styles.saveBtn} onClick={() => setIsStdValueEditMode(true)}>Edit</button>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── REPORT VIEW POPUP ── */}
      {showPfViewModal && pfEditRow && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.65)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 11000, padding: 20 }}>
          <div style={{ width: "90vw", maxWidth: 1100, height: "90vh", background: "#fff", borderRadius: 12, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>{pfEditRow.inspection_no}_InspectionReport.pdf</span>
              <button style={{ border: "none", background: "transparent", fontSize: 22, color: "#64748b", cursor: "pointer" }} onClick={() => setShowPfViewModal(false)}>✖</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", overflowX: "auto", padding: 20, background: "#f8fafc" }}>
              <PfInspectionReport row={pfEditRow} sbeeLogo={sbeeLogo} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════
   PfInspectionReport — renders report from pfEditRow data only.
══════════════════════════════════════════════════════════════ */
function PfInspectionReport({ row, sbeeLogo }) {
  if (!row) return null;
  const th = { border: "1px solid black", padding: "4px", textAlign: "center", fontWeight: "bold", fontSize: "12px" };
  const td = { border: "1px solid black", padding: "4px", fontSize: "11px" };
  const tdLabel = { fontWeight: "bold" };
  const isFinished = (row.product_type || "").toLowerCase().includes("finished");
  const characteristics = row.characteristics || [];

  const getUser = () => {
    try { const p = JSON.parse(localStorage.getItem("user") || "{}"); return p.name || p.username || p.email || ""; } catch {}
    return localStorage.getItem("username") || "";
  };
  const loggedInUser = getUser();

  if (isFinished) {
    const byTestType = characteristics.reduce((acc, c) => {
      const k = (c.test_type || "General").toUpperCase();
      if (!acc[k]) acc[k] = [];
      acc[k].push(c);
      return acc;
    }, {});

    const byProcess = (chars) => chars.reduce((acc, c) => {
      const k = c.process_name || "General";
      if (!acc[k]) acc[k] = [];
      acc[k].push(c);
      return acc;
    }, {});

    const elKey  = Object.keys(byTestType).find(k => k.includes("ELECTRICAL")) || "";
    const dimKey = Object.keys(byTestType).find(k => k.includes("DIMENSIONAL")) || "";

    const TestTable = ({ byProc }) => (
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "35%" }} /><col style={{ width: "18%" }} /><col style={{ width: "9%" }} />
          <col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "14%" }} />
        </colgroup>
        <thead>
          <tr>
            <th rowSpan="2" style={th}>PARTICULARS</th>
            <th rowSpan="2" style={th}>SPECIFIED VALUE</th>
            <th rowSpan="2" style={th}>UOM</th>
            <th colSpan="3" style={th}>TEST RESULTS</th>
            <th rowSpan="2" style={th}>REMARKS</th>
          </tr>
          <tr><th style={th}>SAMPLE 1</th><th style={th}>SAMPLE 2</th><th style={th}>SAMPLE 3</th></tr>
        </thead>
        <tbody>
          {Object.entries(byProc).map(([proc, rows]) => (
            <React.Fragment key={proc}>
              <tr>
                <td colSpan="7" style={{ border: "1px solid black", padding: "8px", fontWeight: "bold", background: "#f5f5f5", fontSize: "12px" }}>
                  {proc.toUpperCase()}
                </td>
              </tr>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...td, paddingLeft: 25 }}>{r.characteristic}</td>
                  <td style={td}>{r.standard_value || ""}</td>
                  <td style={td}>{r.uom || ""}</td>
                  <td style={td} /><td style={td} /><td style={td} /><td style={td} />
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );

    return (
      <div style={{ width: "1000px", margin: "0 auto", border: "1px solid black", background: "#fff", fontFamily: "Calibri" }}>
        <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid black", padding: "15px" }}>
          <img src={sbeeLogo} alt="SBEE" style={{ width: 150, height: "auto" }} />
          <div style={{ flex: 1, textAlign: "center", fontSize: 28, fontWeight: "bold" }}>SBEE CABLES (INDIA) LIMITED</div>
        </div>
        <div style={{ textAlign: "center", fontSize: 22, fontWeight: "bold", borderBottom: "1px solid black", padding: 6 }}>
          {row.inspection_test_type || "INSPECTION REPORT"}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup><col style={{ width: "40%" }} /><col style={{ width: "35%" }} /><col style={{ width: "25%" }} /></colgroup>
          <tbody>
            {[
              [["Construction", ""], ["PRD No", ""], ["Dr. No", ""]],
              [["Product Code", row.product_code], ["Customer Name", ""], ["Drum Type", ""]],
              [["Product Description", row.product_description], ["Cable Type", row.cable_type], ["Wooden / Steel", ""]],
              [["Voltage Grade", row.voltage], ["Date of Testing", new Date().toLocaleDateString("en-GB")], ["Top (M)", ""]],
              [["Specification", row.std_ref], ["Batch No", ""], ["Bottom (M)", ""]],
              [["Temp", ""], ["Drum No", ""], ["Length (M)", ""]],
            ].map((cols, ri) => (
              <tr key={ri}>
                {cols.map(([label, val], ci) => (
                  <td key={ci} style={td}><span style={tdLabel}>{label}</span> : {val}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ textAlign: "center", fontWeight: "bold", border: "1px solid black", padding: 8 }}>ELECTRICAL TESTS</div>
        <TestTable byProc={byProcess(elKey ? byTestType[elKey] : [])} />
        <div style={{ textAlign: "center", fontWeight: "bold", border: "1px solid black", padding: 8, marginTop: 15 }}>DIMENSIONAL TESTS</div>
        <TestTable byProc={byProcess(dimKey ? byTestType[dimKey] : [])} />
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 20 }}>
          <tbody>
            <tr><td style={{ ...td, fontWeight: "bold" }}>CHECKED BY :</td><td style={{ ...td, fontWeight: "bold" }}>APPROVED BY :</td></tr>
            <tr><td style={td}><span style={tdLabel}>Name</span> : {loggedInUser}</td><td style={td}><span style={tdLabel}>Name</span> :</td></tr>
            <tr><td style={td}><span style={tdLabel}>Designation</span> :</td><td style={td}><span style={tdLabel}>Designation</span> :</td></tr>
          </tbody>
        </table>
        <div style={{ display: "flex", justifyContent: "space-between", padding: 15 }}>
          <div><b>Format No: {row.inspection_no}</b></div>
          <div><b>Rev.No &amp; Date: 02 &amp; 10.03.2026</b></div>
        </div>
      </div>
    );
  }

  /* ── RAW MATERIAL ── */
  const byProcess = characteristics.reduce((acc, c) => {
    const k = c.process_name || "General";
    if (!acc[k]) acc[k] = [];
    acc[k].push(c);
    return acc;
  }, {});

  return (
    <div style={{ width: "1000px", margin: "0 auto", border: "2px solid black", background: "#fff", fontFamily: "Calibri" }}>
      <div style={{ display: "flex", alignItems: "center", borderBottom: "2px solid black", padding: "12px 16px" }}>
        <img src={sbeeLogo} alt="SBEE" style={{ width: 150, height: "auto" }} />
        <div style={{ flex: 1, textAlign: "center", fontSize: 26, fontWeight: "bold" }}>SBEE CABLES (INDIA) LIMITED</div>
      </div>
      <div style={{ textAlign: "center", fontSize: 18, fontWeight: "bold", borderBottom: "2px solid black", padding: 8 }}>
        {row.inspection_test_type || "RAW MATERIAL INSPECTION REPORT"}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={{ ...td, width: "33.33%" }}>SUPPLIER :</td>
            <td style={{ ...td, width: "33.33%" }}>GRN No :</td>
            <td style={{ ...td, width: "33.33%" }}>Date of Testing : {new Date().toLocaleDateString("en-GB")}</td>
          </tr>
          <tr>
            <td style={td}>INVOICE No :</td>
            <td style={td}>CATEGORY / SUB CATEGORY : {row.product_category || ""}{row.sub_category ? ` / ${row.sub_category}` : ""}</td>
            <td style={td}>LOT No :</td>
          </tr>
          <tr>
            <td style={td}>PRODUCT CODE : {row.product_code || ""}</td>
            <td style={td}>PRODUCT DESCRIPTION : {row.product_description || ""}</td>
            <td style={td}></td>
          </tr>
          <tr><td style={td}>W.I. No :</td><td style={td}>Ref :</td><td style={td}></td></tr>
        </tbody>
      </table>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th rowSpan="2" style={{ ...th, width: "30%", textAlign: "left", paddingLeft: 10 }}>PARTICULARS</th>
            <th rowSpan="2" style={{ ...th, width: "15%" }}>SPECIFIED VALUE</th>
            <th rowSpan="2" style={{ ...th, width: "8%" }}>UOM</th>
            <th colSpan="3" style={{ ...th, background: "#f5f5f5" }}>SAMPLE</th>
            <th rowSpan="2" style={{ ...th, width: "14%" }}>REMARKS</th>
          </tr>
          <tr>
            <th style={{ ...th, width: "9%" }}>No. 1</th>
            <th style={{ ...th, width: "9%" }}>No. 2</th>
            <th style={{ ...th, width: "9%" }}>No. 3</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(byProcess).map(([proc, rows]) =>
            rows.map((r, i) => (
              <tr key={`${proc}-${i}`}>
                <td style={{ ...td, paddingLeft: 20 }}>{r.characteristic}</td>
                <td style={{ ...td, textAlign: "center" }}>{r.standard_value || ""}</td>
                <td style={{ ...td, textAlign: "center" }}>{r.uom || ""}</td>
                <td style={td} /><td style={td} /><td style={td} /><td style={td} />
              </tr>
            ))
          )}
        </tbody>
      </table>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }}>
        <tbody>
          <tr><td style={{ ...td, fontWeight: "bold", width: "50%" }}>CHECKED BY :</td><td style={{ ...td, fontWeight: "bold", width: "50%" }}>APPROVED BY :</td></tr>
          <tr><td style={{ ...td, height: 36 }}>{loggedInUser}</td><td style={td}></td></tr>
          <tr><td style={{ ...td, height: 36 }}>DESIGNATION</td><td style={td}>DESIGNATION</td></tr>
        </tbody>
      </table>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 16px", fontSize: 11, borderTop: "1px solid black" }}>
        <div><b>Format No: {row.inspection_no}</b></div>
        <div><b>Rev.No &amp; Date: 02 &amp; 10.03.2026</b></div>
      </div>
    </div>
  );
}

export default InspectionProductFormatTab;